/**
 * This model is the individual asset's attribute datum
 * A collection is created `this.attributeDatum` that represents all the data in `this.defaults.attributes`
 * @class
 * @extends Netx.Model
 * @see Netx.Collections.AttributeDatum
 */
import Base from '@netx/core/lib/netx-model';

import AttributeDatumCollection from '../collections/attributeDatum';

export default class AssetAttributeData extends Base {
	/** @type {string} */
	get idAttribute() {
		return 'assetId';
	}

	/**
	 * @property {object}  defaults             - The default attribute values
	 * @property {string}  defaults.assetId     - ID
	 * @property {array}   defaults.attributes  - Attributes
	 */
	get defaults() {
		return {
			// assetId: 0,
			attributes: [
				// Will be passed to and handled by Netx.Collections.AttributeDatum
				// {
				// 	templateId: 0,
				// 	assetId: 0,
				// 	permission: "r",
				// 	values: []
				// }
			],
		};
	}

	/**
	 * @override
	 * @param {object} attr Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs = {}, opts = {}) {
		// Circular reference
		//AttributeDatumCollection = require('../collections/attributeDatum').default;

		super(attrs, opts);

		/**
		 * @memberof module:asset/models/assetAttributeData
		 * @alias assetAttributes
		 * @type {module:asset/collections/attributes}
		 */
		this.assetAttributes = opts.assetAttributes || Netx.attributes;

		/**
		 * @memberof module:asset/models/assetAttributeData
		 * @alias attributeDatum
		 * @type {Netx.Collections.AttributeDatum}
		 */
		this.attributeDatum = new AttributeDatumCollection([], {
			assetId: attrs.assetId || opts.assetId || this.id,
		});

		// Set initial data if passed
		attrs && this.updateAttributeDatum(attrs.attributes);

		// Hey, listen!
		this.listenTo(this, 'change:attributes', (model, newValue, opts) => {
			this.updateAttributeDatum();
		});
	}

	/**
	 * Add an attribute object to this model's `attributes` array
	 * @method
	 * @param {object} attribute Attribute object
	 */
	addAttribute(attribute) {
		const existing = this.attributeDatum.getAttribute(attribute.templateId);
		if (existing) return;

		this.set('attributes', this.get('attributes').concat([attribute]));
	}

	/**
	 * Clear value changes
	 * @method
	 * @see Netx.Collections.AttributeDatum
	 */
	clearChanges() {
		this.attributeDatum.clearChanges();
	}

	/**
	 * Get a single attribute data model
	 * @method
	 * @param {number} tempateId Attribute template id
	 * @param {string} name Template name
	 * @return {Netx.Models.AttributeData}
	 * @see Netx.Collections.AttributeDatum
	 */
	getAttribute(templateId, name) {
		return this.attributeDatum.getAttribute(templateId, name);
	}

	/**
	 * Get a single attribute template model
	 * @method
	 * @param {number} tempateId Attribute template id
	 * @param {string} name Template name
	 * @return {module:asset/models/assetAttribute}
	 * @see Netx.Collections.AttributeDatum
	 */
	getAttributeTemplate(templateId, name) {
		const attributeDataModel = this.getAttribute(templateId, name);
		return attributeDataModel && attributeDataModel.getTemplate();
	}

	/**
	 * Get all attribute value changes
	 * @method
	 * @return {array}
	 * @see Netx.Collections.AttributeDatum
	 */
	getChanges() {
		const changes = this.attributeDatum.getChanges();
		if (!changes || !changes.length) return;

		return {
			assetId: this.id,
			attributes: changes,
		};
	}

	/**
	 * Get a single attribute value
	 * @method
	 * @param {number} templateId Attribute template id
	 * @return {mixed}
	 * @see Netx.Collections.AttributeDatum
	 */
	getValue(templateId) {
		return this.attributeDatum.getValue(templateId);
	}

	/**
	 * Get all attribute values
	 * @method
	 * @return {array}
	 * @see Netx.Collections.AttributeDatum
	 */
	getValues() {
		return this.attributeDatum.getValues();
	}

	/**
	 * Get all read-only attributes
	 * @method
	 * @return {array}
	 * @see Netx.Collections.AttributeDatum
	 */
	getReadOnly() {
		return this.attributeDatum.getReadOnly();
	}

	/**
	 * Get all writable attributes
	 * @method
	 * @return {array}
	 * @see Netx.Collections.AttributeDatum
	 */
	getWritable() {
		return this.attributeDatum.getWritable();
	}

	/**
	 * Remove an attribute object from this model's `attributes` array
	 * @method
	 * @param {number} templateId Template id
	 */
	removeAttribute(templateId) {
		const attributes = this.get('attributes');
		const idx = _.indexOf(
			attributes,
			_.findWhere(attributes, {templateId: templateId}),
		);
		if (idx < 0) return;

		attributes.splice(idx, 1);
		this.set('attributes', attributes);
	}

	/**
	 * Reset all values
	 * @method
	 * @param {object} opts Options
	 * @see Netx.Collections.AttributeDatum
	 */
	resetValues(opts) {
		return this.attributeDatum.resetValues(opts);
	}

	/**
	 * Set value on a single attribute
	 * @method
	 * @param {number} templateId Attribute template id
	 * @param {mixed} val Value
	 * @param {object} opts Options
	 * @see Netx.Collections.AttributeDatum
	 */
	setValue(templateId, val, opts) {
		this.attributeDatum.setValue(templateId, val, opts);
	}

	/**
	 * When this model syncs we populate `this.attributeDatum` based on `this.attributes.attributes`
	 * @method
	 * @param {array} attributes Attributes array
	 * @param {object} opts Options
	 */
	updateAttributeDatum(attributes, opts) {
		attributes = attributes || this.get('attributes');

		const assetAttributes = this.assetAttributes;
		this.attributeDatum.set(
			_.compact(
				_.map(attributes, (attr) => {
					const attrModel = assetAttributes.get(attr.templateId);
					if (!attrModel) return;

					return _.extend(
						{
							templateId: attrModel.id,
						},
						attr,
						_.omit(
							attrModel.attributes,
							'attributeid',
							'objectTypes',
							'templateid',
							'value',
						),
					);
				}),
			),
			_.extend({merge: true}, opts),
		);
	}

	/** @type {string} */
	static get __name__() {
		return 'AssetAttributeData';
	}
}
